import React, { FC } from 'react';
import { nullSafeGet, renderCurrencyByCurrencyId } from '../../utils/DataAccessUtils';

interface SpotPurchaseItemsCellProps {
	items: any[];
	currencyId: number;
	taxAmount?: number | null;
	isPrintView?: boolean;
	showCost?: boolean;
}

export const SpotPurchaseItemsCell: FC<SpotPurchaseItemsCellProps> = ({
	items,
	currencyId,
	taxAmount,
	isPrintView = false,
	showCost = true,
}) => {
	// Every price this cell renders is gated on showCost. CAN_VIEW_PART_EQUIPMENT_COST_IN_WO is
	// what the caller passes here, and hiding only the row total would still leak the same
	// numbers through the per-item unit cost, the line total and the tax line.
	const hasItems = items && items.length > 0;
	if (!hasItems && (!taxAmount || !showCost)) {
		return <span className="bodyregular text-character-secondary">—</span>;
	}
	return (
		<div className="flex flex-col gap-2">
			{(items || []).map((item, index) => {
				const quantity = nullSafeGet('quantity', item) || 0;
				const name = nullSafeGet('name', item) || nullSafeGet('description', item) || '';
				const unitCost = nullSafeGet('unitCost', item) || 0;
				// perUnit is the basis of the unit cost ("each", "per gallon"), so it goes with the
				// price and drops with it.
				const perUnit = nullSafeGet('perUnit', item) || nullSafeGet('uomLabel', item) || '';
				const lineTotal = nullSafeGet('totalCost', item) || quantity * unitCost;
				const lineCurrencyId = nullSafeGet('currencyId', item) || currencyId;
				const unitCostDisplay = renderCurrencyByCurrencyId(lineCurrencyId)(unitCost);
				const totalDisplay = renderCurrencyByCurrencyId(lineCurrencyId)(lineTotal);
				const suffix = perUnit ? `@ ${unitCostDisplay} ${perUnit}` : `@ ${unitCostDisplay}`;
				const fullLabel = showCost
					? perUnit
						? `${quantity} ${name} ${suffix}`
						: `${quantity} × ${name} ${suffix}`
					: `${quantity} × ${name}`;
				return (
					<div key={item.id || `line-${index}`} className="flex flex-row items-baseline gap-3">
						{/* The table cell truncates a long item name behind a tooltip, which a printout
						    cannot reveal — let it wrap instead so the PDF carries the whole label. */}
						<span
							className={
								isPrintView
									? 'bodyregular break-words text-character-primary'
									: 'bodyregular max-w-[260px] truncate text-character-primary'
							}
							title={fullLabel}
						>
							{fullLabel}
						</span>
						{/* The dotted leader only exists to carry the eye to a price — without one it is
						    a rule to nowhere. */}
						{showCost ? (
							<>
								<span className="min-w-[16px] flex-1 border-x-0 border-b border-t-0 border-dotted border-[#d9d9d9]" />
								<span className="bodyregular whitespace-nowrap text-character-primary">
									{totalDisplay}
								</span>
							</>
						) : null}
					</div>
				);
			})}
			{showCost && taxAmount ? (
				<div className="flex flex-row items-baseline gap-3">
					<span className="bodyregular whitespace-nowrap text-character-primary">Tax</span>
					<span className="min-w-[16px] flex-1 border-x-0 border-b border-t-0 border-dotted border-[#d9d9d9]" />
					<span className="bodyregular whitespace-nowrap text-character-primary">
						{renderCurrencyByCurrencyId(currencyId)(taxAmount)}
					</span>
				</div>
			) : null}
		</div>
	);
};

export default SpotPurchaseItemsCell;
